import { Alert, Button, Card, Form, Modal, Row, Skeleton, Switch, Tag } from 'antd';

import FormSelection from 'components/FormSelection/FormSelection';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import intl from 'react-intl-universal';
import styles from './AirbnbHotelForm.module.css';

import { notification } from 'antd';
import { getConstants } from 'utils/apis/constants';
import { getAirbnbListings, getAirbnbs, getRoomTypeIntegrations, linkExistingAirbnb, bulkUnsyncAirbnb } from 'utils/apis/integration';
import { getWebRateByRoomType } from 'utils/apis/rate';
import { getUnits } from 'utils/apis/unit';
import { generatePercentageFromDisplay } from 'utils/general';
import { isAirbnbRoomTypeFeatureEnabled } from 'utils/featureFlags';
import AirbnbAdvanceOptionModal from '../../../OTAListingForm/components/Airbnb/components/AirbnbAdvanceOptionModal';
import OTARateInput from '../../../OTARateInput/OTARateInput';

const STANDARD_RATE_CODE = 'STD';

// Check if a room type connection exists for a master listing
const checkAirbnbHotelRoomConnection = async (propertyId, roomTypeId) => {
  try {
    const response = await getRoomTypeIntegrations(propertyId);
    const roomTypes = response && response.data && response.data.roomTypes ? response.data.roomTypes : [];

    const existingIntegration = roomTypes.find(rt => rt.roomType && (rt.roomType._id === roomTypeId || rt.roomType === roomTypeId));
    const conflictingIntegration = roomTypes.find(rt => rt.roomType && (rt.roomType._id !== roomTypeId && rt.roomType !== roomTypeId));

    return {
      exists: !!existingIntegration || !!conflictingIntegration,
      isEdit: !!existingIntegration,
      hasConflict: !!conflictingIntegration
    };
  } catch (err) {
    return { exists: false, isEdit: false, hasConflict: false };
  }
};

// ----------------------------------------------------------------------
// AirbnbHotelForm Component – new hotel-style integration form for Airbnb listings
// ----------------------------------------------------------------------
class AirbnbHotelForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      roomMapping: [],
      initialRoomMapping: [],
      airbnbListings: [],
      isSaving: false,
      isLoading: true,
      airbnbUsers: [],
      rateModifierTypeConstants: [],
      rateModifierSignConstants: [],
      currency: 'MYR',
      errors: {},
      availableUnits: [],
      selectedMasterUnit: null
    };
  }

  async componentDidMount() {
    const { property, data } = this.props;

    try {
      this.setState({ isLoading: true });

      // Load constants for rate modifiers
      const [rateModifierTypeConstants, rateModifierSignConstants] = await Promise.all([
        getConstants('rateModifierType').then(res => Object.values(res.data)),
        getConstants('rateModifierSign').then(res => Object.values(res.data))
      ]);

      this.setState({
        rateModifierTypeConstants,
        rateModifierSignConstants,
        currency: data && data.propertyCurrency ? data.propertyCurrency : 'MYR'
      });

      // Get available units for this property
      const units = property && (await getUnits(property._id));
      this.setState({ availableUnits: units || [] });

      // Continue with normal initialization
      await this.initializeForm();

      // If we're in edit mode, set the initial state from the existing integration
      if (data && data.roomTypes) {
        const { roomTypes = [] } = data;

        // Get the correct property ID and load rate data from backend
        const correctPropertyId = this.getCorrectPropertyId();

        try {
          // Load fresh rate data from backend with correct property ID
          const response = await getRoomTypeIntegrations(correctPropertyId);

          if (response && response.data && response.data.roomTypes) {
            // Use the rate data from backend response instead of the data prop
            const backendRoomTypes = response.data.roomTypes;

            // Create initial room mappings using backend rate data
            const initialRoomMapping = roomTypes.map(rt => {
              // Find matching room type in backend response
              const backendRoomType = backendRoomTypes.find(brt => brt.roomType._id === rt.roomType._id);

              // Use backend rate data if available, otherwise use default
              let cleanRate = {
                weekday: 0,
                weekend: 0,
                isDerived: false
              };

              if (backendRoomType && backendRoomType.rate) {
                cleanRate = { ...backendRoomType.rate };

                // If it's a derived rate, ensure calculation doesn't have invalid parent object
                if (cleanRate.isDerived && cleanRate.calculation) {
                  cleanRate.calculation = {
                    amount: cleanRate.calculation.amount,
                    type: cleanRate.calculation.type,
                    isPositive: cleanRate.calculation.isPositive
                    // Remove the parent field to avoid ObjectId validation issues
                  };
                }
              }

              return {
                roomType: rt.roomType._id,
                airbnbListingId: rt.airbnbListingId || rt.foreignListingId,
                masterUnitId: rt.masterUnitId,
                connectionExists: true,
                rate: cleanRate,
                advanceOptions: rt.advanceOptions || {},
                isAdvanceOptionModalVisible: false,
                status: backendRoomType.status
              };
            });

            this.setState({
              roomMapping: initialRoomMapping,
              initialRoomMapping: JSON.parse(JSON.stringify(initialRoomMapping)),
              initialData: data
            });
          } else {
            // Fallback to original logic if backend doesn't return rate data
            this.createInitialRoomMappingFallback(roomTypes, data);
          }
        } catch (error) {
          console.error('Error loading backend rate data:', error);
          // Fallback to original logic if backend call fails
          this.createInitialRoomMappingFallback(roomTypes, data);
        }
      }

      // Load rate object last, after we have all necessary data
      await this.loadRateObject();
    } catch (error) {
      Modal.error({
        title: intl.get('integration.error.title').d('Error'),
        content: intl.get('integration.error.initialization').d('Failed to initialize the form.')
      });
    } finally {
      this.setState({ isLoading: false });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { form, propertyHostId } = this.props;
    const { initialData, isLoading, airbnbListings, airbnbUsers } = this.state;

    // Refetch Airbnb users/listings when the selected host filter changes from parent
    if (prevProps.propertyHostId !== propertyHostId) {
      this.refreshAirbnbDataForHost();
    }

    // Only set form values after loading is complete, we have initial data, and airbnb data is loaded
    if (prevState.isLoading && !isLoading && initialData && airbnbListings.length > 0 && airbnbUsers.length > 0) {
      const { roomTypes = [] } = initialData;

      // Set form fields for each room type and update roomMapping with airbnbId
      roomTypes.forEach(rt => {
        // Find the Airbnb listing and get its host ID
        const listing = airbnbListings.find(l => l._id === rt.airbnbListingId || l._id === rt.foreignListingId);
        const airbnbId = listing ? listing.airbnb : null;

        form.setFieldsValue({
          [`masterUnit_${rt.roomType._id}`]: rt.masterUnitId,
          [`airbnbId_${rt.roomType._id}`]: airbnbId,
          [`airbnbListingId_${rt.roomType._id}`]: listing ? listing._id : undefined
        });

        // Update the roomMapping with the airbnbId
        this.setState(prevState => ({
          roomMapping: prevState.roomMapping.map(mapping =>
            mapping.roomType === rt.roomType._id
              ? {
                  ...mapping,
                  masterUnitId: rt.masterUnitId,
                  airbnbId,
                  airbnbListingId: listing ? listing._id : null
                }
              : mapping
          )
        }));
      });

      // Clear initialData after setting form values
      this.setState({ initialData: null });
    }
  }

  refreshAirbnbDataForHost = async () => {
    const { propertyHostId, form } = this.props;

    try {
      this.setState({ isLoading: true });

      // Fetch Airbnb users and listings for the newly selected host
      const [airbnbUsers, airbnbListings] = await Promise.all([getAirbnbs({ host: propertyHostId }), getAirbnbListings({ hosts: propertyHostId })]);

      // Update available options
      this.setState({
        airbnbUsers: airbnbUsers.data || [],
        airbnbListings: airbnbListings.data || []
      });

      // Clear Airbnb host and listing selections for all mapped room types
      const { roomMapping } = this.state;
      const clearedMapping = roomMapping.map(m => ({
        ...m,
        airbnbId: null,
        airbnbListingId: null,
        foreignListingId: null,
        connectionExists: false,
        isEdit: false,
        hasConflict: false
      }));

      this.setState({ roomMapping: clearedMapping });

      // Clear corresponding form fields
      const fieldsToClear = {};
      clearedMapping.forEach(m => {
        fieldsToClear[`airbnbId_${m.roomType}`] = undefined;
        fieldsToClear[`airbnbListingId_${m.roomType}`] = undefined;
      });
      form.setFieldsValue(fieldsToClear);
    } catch (error) {
      Modal.error({
        title: intl.get('integration.error.title').d('Error'),
        content: intl.get('integration.error.initialization').d('Failed to refresh Airbnb data for the selected host.')
      });
    } finally {
      this.setState({ isLoading: false });
    }
  };

  initializeForm = async () => {
    try {
      const { propertyHostId, data } = this.props;
      const [airbnbUsers, airbnbListings] = await Promise.all([getAirbnbs({ host: propertyHostId }), getAirbnbListings({ hosts: propertyHostId })]);

      // Store the fetched data
      this.setState({
        airbnbUsers: airbnbUsers.data || [],
        airbnbListings: airbnbListings.data || []
      });

      // If we have initial data, set the Airbnb host ID first
      if (data && data.airbnbId) {
        const { form } = this.props;
        form.setFieldsValue({ airbnbId: data.airbnbId });
      }
    } catch (error) {
      Modal.error({
        title: intl.get('integration.error.title').d('Error'),
        content: intl.get('integration.error.initialization').d('Failed to initialize the form.')
      });
    }
  };

  loadRateObject = async () => {
    const { listingIntegration, data } = this.props;
    const { roomMapping } = this.state;

    try {
      // For room type integrations, rate data is already loaded in componentDidMount from data.roomTypes[].rate
      // Only load web rates if we don't have any room type integration data
      if (data && data.roomTypes && data.roomTypes.length > 0) {
        // Rate data is already loaded from room type integrations in componentDidMount
        // Don't override it here
        return;
      }

      // For unit-level integrations, check listingIntegration.rate
      if (listingIntegration && listingIntegration.rate) {
        const { rate } = listingIntegration;
        // Ensure isDerived is boolean and clean up calculation
        const cleanRate = {
          ...rate,
          isDerived: !!rate.isDerived
        };

        // If it's a derived rate, clean up the calculation object
        if (cleanRate.isDerived && cleanRate.calculation) {
          cleanRate.calculation = {
            amount: cleanRate.calculation.amount,
            type: cleanRate.calculation.type,
            isPositive: cleanRate.calculation.isPositive
            // Remove parent field to avoid ObjectId issues
          };
        }

        // Update all room mappings with the same rate initially
        this.setState(prevState => ({
          roomMapping: prevState.roomMapping.map(mapping => ({
            ...mapping,
            rate: cleanRate
          }))
        }));
      } else {
        // Only load web rates if no integration data exists
        await this.getWebRate();
      }
    } catch (error) {
      console.error('Error in loadRateObject:', error);
      // Set a default rate object if we can't load one
      this.setState(prevState => ({
        roomMapping: prevState.roomMapping.map(mapping => ({
          ...mapping,
          rate: {
            weekday: 0,
            weekend: 0,
            isDerived: false
          }
        }))
      }));
    }
  };

  getWebRate = async () => {
    const { property } = this.props;
    const { roomMapping } = this.state;

    // Guard against missing property or roomTypes
    if (!property || !property.roomTypes || !property.roomTypes.length) {
      return;
    }

    try {
      // Get web rates for each room type in the mapping
      const updatedRoomMapping = await Promise.all(
        roomMapping.map(async mapping => {
          try {
            const webRates = await getWebRateByRoomType(mapping.roomType);
            const stdRate = webRates.data.find(rate => rate.code === STANDARD_RATE_CODE);

            if (stdRate) {
              const { weekday, weekend, isDerived } = stdRate;
              return {
                ...mapping,
                rate: { weekday, weekend, isDerived }
              };
            }
            return {
              ...mapping,
              rate: { weekday: 0, weekend: 0, isDerived: false }
            };
          } catch (error) {
            return {
              ...mapping,
              rate: { weekday: 0, weekend: 0, isDerived: false }
            };
          }
        })
      );

      this.setState({ roomMapping: updatedRoomMapping });
    } catch (error) {
      this.setState(prevState => ({
        roomMapping: prevState.roomMapping.map(mapping => ({
          ...mapping,
          rate: { weekday: 0, weekend: 0, isDerived: false }
        }))
      }));
    }
  };

  getWebRateForRoomType = async roomTypeId => {
    const { property } = this.props;
    const { roomMapping } = this.state;

    if (!property || !property.roomTypes || !property.roomTypes.length) {
      return;
    }

    try {
      const mappingIndex = roomMapping.findIndex(m => m.roomType === roomTypeId);
      if (mappingIndex === -1) return;

      const webRates = await getWebRateByRoomType(roomTypeId);
      const stdRate = webRates.data.find(rate => rate.code === STANDARD_RATE_CODE);
      const newRate = stdRate
        ? { weekday: stdRate.weekday, weekend: stdRate.weekend, isDerived: stdRate.isDerived }
        : { weekday: 0, weekend: 0, isDerived: false };

      this.setState(prevState => {
        const updated = [...prevState.roomMapping];
        updated[mappingIndex] = { ...updated[mappingIndex], rate: newRate };
        return { roomMapping: updated };
      });
    } catch (error) {
      this.setState(prevState => {
        const mappingIndex = prevState.roomMapping.findIndex(m => m.roomType === roomTypeId);
        if (mappingIndex === -1) return null;
        const updated = [...prevState.roomMapping];
        updated[mappingIndex] = { ...updated[mappingIndex], rate: { weekday: 0, weekend: 0, isDerived: false } };
        return { roomMapping: updated };
      });
    }
  };

  updateRate = roomTypeId => newRates => {
    this.setState(prevState => ({
      roomMapping: prevState.roomMapping.map(mapping =>
        mapping.roomType === roomTypeId ? { ...mapping, rate: { ...mapping.rate, ...newRates } } : mapping
      )
    }));
  };

  generateAirbnbUserOptions = () => {
    const { airbnbUsers } = this.state;
    return airbnbUsers.map(airbnbUser => ({
      value: airbnbUser._id,
      displayValue: `${airbnbUser.userId} | ${airbnbUser.nickname || intl.get('hostConnect.integration.message.nickname').d('Nickname Not Set')}`,
      key: airbnbUser._id
    }));
  };

  generateAirbnbListingOptions = (airbnbId, currentRoomTypeId) => {
    const { airbnbListings, roomMapping } = this.state;
    if (!airbnbId) return [];

    // Get all selected listing IDs except for the current room type
    const selectedListingIds = roomMapping.filter(m => m.roomType !== currentRoomTypeId && m.airbnbListingId).map(m => m.airbnbListingId);

    // Filter out listings that are already selected in other room types
    return airbnbListings
      .filter(listing => !listing.unit && listing.airbnb === airbnbId && !selectedListingIds.includes(listing._id))
      .map(listing => ({
        value: listing._id,
        displayValue: `${listing.listingId} | ${(listing.data && listing.data.listing && listing.data.listing.name) ||
          intl.get('hostConnect.integration.message.noListing').d('No Listing Name')}`,
        key: listing._id
      }));
  };

  /**
   * Toggle whether to sync a room type.
   * If toggled on, add a mapping record with a null master listing.
   * If toggled off, remove the mapping record.
   */
  handleOnToggleRoomSync = roomTypeId => checked => {
    this.setState(
      prevState => {
        let newRoomMapping;
        if (checked) {
          // Check if mapping already exists
          const existingMapping = prevState.roomMapping.find(m => m.roomType === roomTypeId);

          if (!existingMapping) {
            // Add new mapping
            newRoomMapping = [
              ...prevState.roomMapping,
              {
                roomType: roomTypeId,
                masterUnitId: null,
                airbnbId: null,
                airbnbListingId: null,
                connectionExists: false,
                rate: {
                  weekday: 0,
                  weekend: 0,
                  isDerived: false
                },
                advanceOptions: {},
                isAdvanceOptionModalVisible: false,
                status: true
              }
            ];
          } else {
            newRoomMapping = prevState.roomMapping;
          }
        } else {
          // Remove mapping
          newRoomMapping = prevState.roomMapping.filter(m => m.roomType !== roomTypeId);
        }

        return { roomMapping: newRoomMapping };
      },
      () => {
        if (checked) {
          this.getWebRateForRoomType(roomTypeId);
        }
      }
    );
  };

  // Helper method to get the correct property ID
  getCorrectPropertyId = () => {
    const { data, property } = this.props;

    // First try to get property ID from data.foreignPropertyId (this is the correct one)
    if (data && data.foreignPropertyId) {
      return data.foreignPropertyId;
    }

    // If we have data with room types, try to get the property ID from room type data
    if (data && data.roomTypes && data.roomTypes.length > 0) {
      const roomType = data.roomTypes[0];
      if (roomType.roomType && roomType.roomType.property) {
        const correctPropertyId = roomType.roomType.property._id || roomType.roomType.property;
        return correctPropertyId;
      }
    }

    // Fallback to the property prop (might be wrong)
    return property._id;
  };

  /**
   * Handle selection change in the searchable dropdown for the Airbnb master listing.
   * After selecting a master listing, we call the check for an already existing room type connection.
   */
  handleOnSelectAirbnbForRoom = roomTypeId => selectedListingId => {
    let { roomMapping } = this.state;
    const { property } = this.props;
    const mappingIndex = roomMapping.findIndex(m => m.roomType === roomTypeId);
    if (mappingIndex !== -1) {
      roomMapping[mappingIndex].airbnbListingId = selectedListingId;
      checkAirbnbHotelRoomConnection(this.getCorrectPropertyId(), roomTypeId)
        .then(res => {
          roomMapping[mappingIndex] = {
            ...roomMapping[mappingIndex],
            connectionExists: res.exists,
            isEdit: res.isEdit,
            hasConflict: res.hasConflict
          };
          this.setState({ roomMapping });
        })
        .catch(err => {
          console.error('Failed to check room type connection:', err);
        });
    }
  };

  validateForm = () => {
    const { roomMapping, errors } = this.state;
    const newErrors = {};

    if (roomMapping.length === 0) {
      newErrors.roomMapping = intl.get('integration.error.noRoomTypes').d('Please select at least one room type to sync');
    }

    // Validate each enabled room type has all required fields
    roomMapping.forEach(mapping => {
      if (!mapping.masterUnitId) {
        newErrors[`masterUnit_${mapping.roomType}`] = intl.get('integration.error.requiredMasterUnit').d('Please select a master unit');
      }
      if (!mapping.airbnbId) {
        newErrors[`airbnbId_${mapping.roomType}`] = intl.get('integration.error.requiredAirbnbId').d('Please select an Airbnb ID');
      }
      if (!mapping.airbnbListingId) {
        newErrors[`airbnbListingId_${mapping.roomType}`] = intl.get('integration.error.requiredListing').d('Please select an Airbnb listing');
      }
    });

    this.setState({ errors: { ...errors, ...newErrors } });
    return Object.keys(newErrors).length === 0;
  };

  handleMasterUnitChange = (roomTypeId, unitId) => {
    let { roomMapping } = this.state;
    const mappingIndex = roomMapping.findIndex(m => m.roomType === roomTypeId);

    if (mappingIndex !== -1) {
      roomMapping[mappingIndex].masterUnitId = unitId;
      this.setState({ roomMapping });
    }
  };

  handleListingChange = (roomTypeId, listingId) => {
    let { roomMapping } = this.state;
    const { property } = this.props;
    const mappingIndex = roomMapping.findIndex(m => m.roomType === roomTypeId);

    if (mappingIndex !== -1) {
      roomMapping[mappingIndex].airbnbListingId = listingId;
      roomMapping[mappingIndex].foreignListingId = listingId;

      // Check for existing connections
      checkAirbnbHotelRoomConnection(this.getCorrectPropertyId(), roomTypeId)
        .then(res => {
          roomMapping[mappingIndex] = {
            ...roomMapping[mappingIndex],
            connectionExists: res.exists,
            isEdit: res.isEdit,
            hasConflict: res.hasConflict
          };
          this.setState({ roomMapping });
        })
        .catch(err => {
          console.error('Failed to check room type connection:', err);
        });
    }
  };

  handleAdvanceOptionsClick = roomTypeId => () => {
    const { property } = this.props;
    const { roomMapping } = this.state;

    const mapping = roomMapping.find(m => m.roomType === roomTypeId);
    if (!mapping || !mapping.masterUnitId || !mapping.airbnbListingId) {
      return;
    }

    const roomTypeObj = property.roomTypes.find(rt => rt._id === roomTypeId);
    if (roomTypeObj && roomTypeObj._id) {
      this.setState(prevState => ({
        roomMapping: prevState.roomMapping.map(m => (m.roomType === roomTypeId ? { ...m, isAdvanceOptionModalVisible: true } : m))
      }));
    }
  };

  handleAdvanceOptionsClose = roomTypeId => () => {
    this.setState(prevState => ({
      roomMapping: prevState.roomMapping.map(m => (m.roomType === roomTypeId ? { ...m, isAdvanceOptionModalVisible: false } : m))
    }));
  };

  handleAdvanceOptionsSubmit = roomTypeId => advanceOptions => {
    // Process rate distribution payload like in AirbnbForm
    const processedRate = this.massageProcessedRate(advanceOptions.rateDistributionPayload);

    this.setState(prevState => ({
      roomMapping: prevState.roomMapping.map(m =>
        m.roomType === roomTypeId
          ? {
              ...m,
              advanceOptions,
              isAdvanceOptionModalVisible: false,
              rate: processedRate
            }
          : m
      )
    }));
  };

  massageProcessedRate = rateDistributionPayload => {
    const { rate, rateModifierSignConstants, rateModifierTypeConstants } = this.state;

    let processedRate = {};
    const rateObject = rateDistributionPayload.length > 0 && rateDistributionPayload[0].rates.length > 0 && rateDistributionPayload[0].rates[0];
    if (!!rateObject) {
      processedRate = {
        weekday: rateObject.rate.weekdayRate,
        weekend: rateObject.rate.weekendRate,
        isDerived: rateObject.rate.isDerived
      };
      const isDerived = rateObject.rate.isDerived;
      if (!!isDerived) {
        const isPercentage = !!rateModifierTypeConstants.find(type => type.code === rateObject.rate.modifierType && type.isPercentage);
        const isPositive = !!rateModifierSignConstants.find(sign => sign.code === rateObject.rate.modifierSign && sign.isPositive);
        let amount = rateObject.rate.modifierAmount;

        amount = isPercentage ? generatePercentageFromDisplay(amount) : amount;

        processedRate.calculation = {
          amount,
          type: rateObject.rate.modifierType,
          isPositive
        };
      }
    } else {
      processedRate = rate;
    }

    return processedRate;
  };

  onConfirm = async () => {
    if (!this.validateForm()) {
      return;
    }

    const { roomMapping, initialRoomMapping } = this.state;
    const { onAfterSave, onClose } = this.props;

    this.setState({ isSaving: true });

    try {
      // Find room types to unsync
      const roomTypesToUnsync = initialRoomMapping.filter(
        initialMapping => !roomMapping.some(currentMapping => currentMapping.roomType === initialMapping.roomType)
      );

      if (roomTypesToUnsync.length > 0) {
        const mappingsToUnsync = roomTypesToUnsync.map(mapping => ({
          listingId: mapping.airbnbListingId,
          roomTypeId: mapping.roomType
        }));
        await bulkUnsyncAirbnb(mappingsToUnsync);
      }

      // Create room type integrations using linkExistingAirbnb
      if (roomMapping.length > 0) {
        await Promise.all(
          roomMapping.map(async mapping => {
            return linkExistingAirbnb(
              null, // unitId is null for room type integration
              mapping.airbnbListingId, // Use the room-specific listing ID
              'sync_all',
              mapping.rate,
              {
                ...mapping.advanceOptions,
                isRoomTypeInventory: true,
                roomTypeId: mapping.roomType,
                masterUnitId: mapping.masterUnitId
              },
              true // Always partial sync for room type inventory
            );
          })
        );
      }

      if (onAfterSave) {
        onAfterSave();
        onClose();
      }

      this.setState({ isSaving: false });
      notification.success({
        message: intl.get('integration.success.roomTypeSync').d('Room type integration configuration saved successfully')
      });
    } catch (error) {
      this.setState({ isSaving: false });
      notification.error({
        message: intl.get('integration.error.title').d('Error'),
        description: intl.get('integration.error.save').d('Failed to save room type integrations.')
      });
    }
  };

  handleAirbnbHostChange = roomTypeId => value => {
    const { form } = this.props;

    // Clear the Airbnb listing selection for this room
    form.setFieldsValue({
      [`airbnbListingId_${roomTypeId}`]: undefined
    });

    // Update state to reflect the host change and clear listing
    this.setState(prevState => ({
      roomMapping: prevState.roomMapping.map(mapping =>
        mapping.roomType === roomTypeId
          ? {
              ...mapping,
              airbnbId: value,
              airbnbListingId: null
            }
          : mapping
      )
    }));
  };

  // Fallback method to create initial room mapping without backend rate data
  createInitialRoomMappingFallback = (roomTypes, data) => {
    const initialRoomMapping = roomTypes.map(rt => {
      // Clean up the rate object to ensure it doesn't have invalid calculation.parent data
      let cleanRate = rt.rate || {
        weekday: 0,
        weekend: 0,
        isDerived: false
      };

      // If it's a derived rate, ensure calculation doesn't have invalid parent object
      if (cleanRate.isDerived && cleanRate.calculation) {
        cleanRate = {
          ...cleanRate,
          calculation: {
            amount: cleanRate.calculation.amount,
            type: cleanRate.calculation.type,
            isPositive: cleanRate.calculation.isPositive
            // Remove the parent field to avoid ObjectId validation issues
          }
        };
      }

      return {
        roomType: rt.roomType._id,
        airbnbListingId: rt.airbnbListingId || rt.foreignListingId,
        masterUnitId: rt.masterUnitId,
        connectionExists: true,
        rate: cleanRate,
        advanceOptions: rt.advanceOptions || {},
        isAdvanceOptionModalVisible: false,
        status: true
      };
    });

    this.setState({
      roomMapping: initialRoomMapping,
      initialRoomMapping: JSON.parse(JSON.stringify(initialRoomMapping)),
      initialData: data
    });
  };

  renderRoomMapping = () => {
    const { roomMapping, errors, availableUnits, isSaving, airbnbListings } = this.state;
    const { property, form } = this.props;

    if (!property || !property.roomTypes || !form) return null;

    const airbnbUserOptions = this.generateAirbnbUserOptions();

    return (
      <>
        {property.roomTypes.map(roomType => {
          const mapping = roomMapping.find(m => m.roomType === roomType._id);
          const isSynced = !!mapping;

          // Filter units for this room type
          const roomTypeUnits = availableUnits.filter(unit => unit.roomType && unit.roomType._id === roomType._id);

          // Generate unit options for this room type
          const unitOptions = roomTypeUnits.map(unit => ({
            value: unit._id,
            displayValue: unit.name,
            key: unit._id
          }));

          // Get Airbnb listing options based on selected host, passing current room type ID
          const airbnbListingOptions = mapping ? this.generateAirbnbListingOptions(mapping.airbnbId, roomType._id) : [];

          return (
            <Row key={roomType._id} className="ota-row" style={{ marginBottom: '20px' }}>
              <Card
                className="ota-card"
                title={
                  <Row style={{ marginBottom: 10 }}>
                    <span style={{ marginRight: 10 }}>{roomType.name}</span>
                    <Switch checked={isSynced} onChange={this.handleOnToggleRoomSync(roomType._id)} disabled={isSaving} />{' '}
                    {isSynced ? (
                      mapping.status === 'error' ? (
                        <Tag color="red">{intl.get('integration.tag.error').d('Error')}</Tag>
                      ) : mapping.status ? (
                        <Tag color="green">{intl.get('integration.tag.synced').d('Synced')}</Tag>
                      ) : (
                        <Tag color="red">{intl.get('integration.tag.error').d('Error')}</Tag>
                      )
                    ) : (
                      <Tag color="red">{intl.get('integration.tag.notSynced').d('Not Synced')}</Tag>
                    )}
                  </Row>
                }
              >
                {isSynced && (
                  <>
                    <FormSelection
                      form={form}
                      formLabel={intl.get('hostConnect.integration.headerLabels.masterUnit').d('Master Unit')}
                      name={`masterUnit_${roomType._id}`}
                      requiredErrorMessage={intl.get('hostConnect.integration.placeholder.selectMasterUnitMsg').d('Please select a master unit')}
                      placeholder={intl.get('hostConnect.integration.placeholder.selectMasterUnit').d('Select Master Unit')}
                      selections={unitOptions}
                      onChange={value => this.handleMasterUnitChange(roomType._id, value)}
                      value={mapping && mapping.masterUnitId}
                      error={errors[`masterUnit_${roomType._id}`]}
                      className={styles.formSelectionNoMargin}
                      disabled={isSaving}
                    />

                    <FormSelection
                      form={form}
                      formLabel={intl.get('hostConnect.integration.headerLabels.selectAirbnbId').d('Select Airbnb Host')}
                      name={`airbnbId_${roomType._id}`}
                      requiredErrorMessage={intl.get('hostConnect.integration.placeholder.selectAirbnbIdMsg').d('This is required')}
                      placeholder={intl.get('hostConnect.integration.placeholder.selectAirbnbId').d('Airbnb Host ID')}
                      selections={airbnbUserOptions}
                      onChange={this.handleAirbnbHostChange(roomType._id)}
                      value={mapping && mapping.airbnbId}
                      error={errors[`airbnbId_${roomType._id}`]}
                      className={styles.formSelectionNoMargin}
                      disabled={isSaving}
                    />

                    <FormSelection
                      form={form}
                      formLabel={intl.get('hostConnect.integration.headerLabels.selectListingAirbnb').d('Select Airbnb Listing')}
                      name={`airbnbListingId_${roomType._id}`}
                      requiredErrorMessage={intl.get('hostConnect.integration.placeholder.selectAirbnbIdMsg').d('This is required')}
                      placeholder={intl.get('hostConnect.integration.placeholder.selectListingAirbnb').d('Select Available Units')}
                      selections={airbnbListingOptions}
                      onChange={value => this.handleListingChange(roomType._id, value)}
                      value={mapping && mapping.airbnbListingId}
                      disabled={!mapping.airbnbId || isSaving}
                      error={errors[`airbnbListingId_${roomType._id}`]}
                      className={styles.formSelectionNoMargin}
                    />

                    {mapping && mapping.connectionExists && mapping.hasConflict && (
                      <Alert
                        style={{ marginTop: 10 }}
                        message={intl
                          .get('integration.warning.existingConnection')
                          .d('Warning: This Airbnb listing is already connected to a different room type.')}
                        description={intl
                          .get('integration.warning.existingConnectionDesc')
                          .d('Creating this connection may affect the existing room type integration.')}
                        type="warning"
                        showIcon
                      />
                    )}
                    {mapping && mapping.connectionExists && mapping.isEdit && (
                      <Alert
                        style={{ marginTop: 10 }}
                        message={intl.get('integration.info.editingConnection').d('You are editing an existing room type connection.')}
                        type="info"
                        showIcon
                      />
                    )}

                    <Row style={{ marginTop: '20px' }}>
                      <OTARateInput
                        rate={mapping.rate || {}}
                        updateRates={this.updateRate(roomType._id)}
                        currency={this.state.currency}
                        disabled={isSaving}
                      />
                    </Row>

                    <Row style={{ marginTop: '20px' }}>
                      <Button
                        onClick={this.handleAdvanceOptionsClick(roomType._id)}
                        disabled={!mapping.masterUnitId || !mapping.airbnbListingId || isSaving}
                      >
                        {intl.get('hostConnect.integration.headerLabels.advanced').d('Advanced Options')}
                      </Button>
                    </Row>

                    {mapping.isAdvanceOptionModalVisible && (
                      <AirbnbAdvanceOptionModal
                        isVisible={mapping.isAdvanceOptionModalVisible}
                        onClose={this.handleAdvanceOptionsClose(roomType._id)}
                        onConfirm={this.handleAdvanceOptionsSubmit(roomType._id)}
                        defaultValues={mapping.advanceOptions}
                        rateModifierTypeConstants={this.state.rateModifierTypeConstants}
                        rateModifierSignConstants={this.state.rateModifierSignConstants}
                        roomType={roomType}
                        rate={mapping.rate || {}}
                        currency={this.state.currency}
                        airbnbListingId={mapping.airbnbListingId}
                        unitId={mapping.masterUnitId}
                        isFullSync={false}
                        disabled={isSaving}
                      />
                    )}
                  </>
                )}
              </Card>
            </Row>
          );
        })}
      </>
    );
  };

  render() {
    const { isLoading, isSaving, errors, availableUnits, airbnbListings } = this.state;
    const { propertyHostId } = this.props;

    // Feature gate: Check if Airbnb Room Type feature is enabled for this host
    if (!isAirbnbRoomTypeFeatureEnabled(propertyHostId)) {
      return (
        <Card>
          <Alert
            message={intl.get('integration.featureNotAvailable').d('Feature Not Available')}
            description={intl
              .get('integration.airbnbRoomTypeNotAvailable')
              .d('Airbnb room type integration is not available for your account at this time.')}
            type="info"
            showIcon
          />
        </Card>
      );
    }

    // Check if any unit in the property has an Airbnb listing sync
    const hasAnyUnitSync = availableUnits.some(unit => airbnbListings.some(listing => unit._id === listing.unit));

    // If any unit has a listing sync, show warning and disable form
    if (hasAnyUnitSync) {
      return (
        <Card>
          <Alert
            message={intl.get('integration.warning.existingUnitSync').d('Cannot create room type connections')}
            description={
              <div>
                {intl.get('integration.warning.unitSyncExists').d('This property has unit-level Airbnb integrations.')}
                {intl
                  .get('integration.warning.removeUnitSync')
                  .d('Please remove all unit-level integrations first before creating room type connections.')}
              </div>
            }
            type="error"
            showIcon
          />
        </Card>
      );
    }

    return (
      <Row className="scroll-bar-style">
        <Card>
          <Form disabled={isSaving}>
            {!isLoading ? (
              <>
                {this.renderRoomMapping()}
                {errors.roomMapping && <Alert style={{ marginBottom: 10 }} message={errors.roomMapping} type="error" showIcon />}
                <Row className="ota-row" style={{ marginTop: 20 }}>
                  <Button type="primary" onClick={this.onConfirm} disabled={isSaving} loading={isSaving}>
                    {isSaving ? intl.get('integration.button.saving').d('Saving...') : intl.get('integration.button.confirm').d('Confirm')}
                  </Button>
                </Row>
              </>
            ) : (
              <Skeleton active={true} />
            )}
          </Form>
        </Card>
      </Row>
    );
  }
}

AirbnbHotelForm.propTypes = {
  property: PropTypes.object.isRequired,
  onAfterSave: PropTypes.func,
  hotelIntegrations: PropTypes.array,
  selectedIntegrationSourceCode: PropTypes.string,
  form: PropTypes.object,
  listingIntegration: PropTypes.object,
  history: PropTypes.object,
  checkIsAdminReadOnly: PropTypes.func,
  propertyHostId: PropTypes.string
};

export default Form.create()(AirbnbHotelForm);
